import type { MutableRefObject, RefObject } from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const BREADCRUMB_GAP_PX = 8;
const HYSTERESIS_PX = 8;

export interface UseBreadcrumbOverflowReturn {
    containerRef: RefObject<HTMLDivElement | null>;
    contentRef: RefObject<HTMLDivElement | null>;
    sectionRefs: MutableRefObject<Partial<Record<string, HTMLDivElement | null>>>;
    collapsedCount: number;
    collapsedSet: Set<string>;
}

/**
 * Collapses breadcrumb sections, in `collapsibleOrder`, until the row fits its container.
 * `contentKey` must change whenever a visible label does, so the fit is measured again.
 */
export function useBreadcrumbOverflow(
    collapsibleOrder: string[],
    contentKey: string
): UseBreadcrumbOverflowReturn {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const sectionRefs = useRef<Partial<Record<string, HTMLDivElement | null>>>({});
    const cachedWidths = useRef<Partial<Record<string, number>>>({});
    const measuredKey = useRef(contentKey);
    const [collapsedCount, setCollapsedCount] = useState(0);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        setCollapsedCount((c) => Math.min(c, collapsibleOrder.length));
    }, [collapsibleOrder.length]);

    // ResizeObserver: track container width for overflow detection
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const width = entry.contentRect.width;
                setContainerWidth(width);
            }
        });
        observer.observe(container);
        setContainerWidth(container.getBoundingClientRect().width);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;
        if (!container || !content || containerWidth <= 0) return;

        // Sections often collapse while they still show a wide placeholder, and the width cached
        // for them then keeps the loaded, shorter label hidden. Fit again from fully expanded.
        // The reset takes a pass of its own: measuring with the old count in the same pass
        // would queue a step on top of it. Both passes run before paint, so nothing flickers.
        if (measuredKey.current !== contentKey) {
            measuredKey.current = contentKey;
            cachedWidths.current = {};
            if (collapsedCount > 0) {
                setCollapsedCount(0);
                return;
            }
        }

        const contentWidth = content.scrollWidth;
        const availableWidth = Math.floor(containerWidth);

        if (contentWidth >= availableWidth && collapsedCount < collapsibleOrder.length) {
            const nextToCollapse = collapsibleOrder[collapsedCount];
            const sectionEl = sectionRefs.current[nextToCollapse];
            if (sectionEl) {
                const w = sectionEl.getBoundingClientRect().width;
                cachedWidths.current[nextToCollapse] = w;
                setCollapsedCount((c) => Math.min(c + 1, collapsibleOrder.length));
            }
            return;
        }

        if (collapsedCount > 0) {
            const nextToShow = collapsibleOrder[collapsedCount - 1];
            const cachedW = cachedWidths.current[nextToShow] ?? 0;
            if (contentWidth + cachedW + BREADCRUMB_GAP_PX + HYSTERESIS_PX <= availableWidth) {
                setCollapsedCount((c) => Math.max(c - 1, 0));
            }
        }
    }, [containerWidth, collapsedCount, collapsibleOrder, contentKey]);

    const collapsedSet = useMemo(
        () => new Set(collapsibleOrder.slice(0, collapsedCount)),
        [collapsibleOrder, collapsedCount]
    );

    return {
        containerRef,
        contentRef,
        sectionRefs,
        collapsedCount,
        collapsedSet,
    };
}
